import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';

import { Modal, Input, Table, Tag, InputNumber, Select, Tooltip, Drawer } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined, QuestionCircleOutlined, CheckOutlined } from '@ant-design/icons';
import { NotificationInstance } from 'antd/es/notification/interface';

import PreloadingTotals from '@flc/components/logistics/import/preloading/preloading-totals';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { exception, securityRestriction } from '@extensions/notification';
import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { delayAction } from '@extensions/utils';

import IBaseComponentProps from '@components/base-component-props';

import { IUserSession } from '@entities/user-session';
import { ICity } from '@entities/city';
import { IPreloadingFilter } from '@entities/preloading-filter';
import { IPreloadingLine } from '@entities/preloading-line';
import { ITruck } from '@entities/truck';
import { ICountry } from '@entities/country';

import { Permission, hasPermission } from '@enums/permission';
import { PreloadingStatus } from '@enums/preloading-status';

import { SumIcon } from '@icons/index';

const filterContext: string = 'ConsignmentImportPreloading';

interface IConsignmentImportPreloadingProps extends IBaseComponentProps {
    truck?: ITruck;
    cities: Array<ICity>;
    countries: Array<ICountry>;
    api: NotificationInstance;
}

const ConsignmentImportPreloading = (props: IConsignmentImportPreloadingProps) => {
    const { truck, countries, cities, api, serverFetch } = props;

    const initFilter: IPreloadingFilter = {
        statuses: [PreloadingStatus.Processed],
        countryIds: countries.map((c) => {
            return c.id || '';
        }),
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const d = useAppDispatch();

    const [modal, modalCntextHolder] = Modal.useModal();

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<IPreloadingFilter>((s) => s.filters[filterContext]);

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);

    const [lines, setLines] = useState<Array<IPreloadingLine>>([]);

    const [loading, setLoading] = useState<boolean>(false);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [showFilter, setShowFilter] = useState<boolean>(true);
    const [openTotals, setOpenTotals] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        setSelectedIds([]);

        setRefreshRequired(false);
        setLoading(true);

        serverFetch(`preloading/lines`, { method: 'GET', queryParams: filter })
            .then((data: Array<IPreloadingLine>) => {
                setLines(data);
                setLoading(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения груза', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onLoadCompleat = () => {
        setLoading(true);

        const result: Array<IPreloadingLine> = [];
        selectedIds.map((id) => {
            const line = lines.find((l) => l.id == id);
            if (line) result.push(line);
        });

        serverFetch(`consignments/${truck?.id}/load`, { method: 'POST', bodyData: result })
            .then(() => {
                setRefreshRequired(true);
                setLoading(false);
            })
            .catch((ex: any) => {
                setLoading(false);
                exception(api, 'Ошибка добавления груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Загрузить',
                        disabled: selectedIds.length <= 0,
                        key: 'complete',
                        type: 'primary',
                        icon: <CheckOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTruckConsignment)) {
                                securityRestriction(api, [Permission.ManageTruckConsignment]);
                                return;
                            }

                            modal.confirm({
                                title: `Загрузить выбранный груз    ?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onLoadCompleat();
                                },
                            });
                        },
                    },
                    {
                        label: 'Итого',
                        key: 'add',
                        icon: <SumIcon style={{ fontSize: 16 }} />,
                        onClick: () => {
                            setOpenTotals(true);
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 200 }}
                            key='awbNumber'
                            placeholder='AWB'
                            value={filter?.awbNumber}
                            onChange={(data: React.ChangeEvent<HTMLInputElement>) => {
                                d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            key='markingCode'
                            style={{ width: 130 }}
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <InputNumber
                            style={{ width: 130 }}
                            key='consigneeCode'
                            placeholder='ID клиента'
                            value={filter?.consigneeCode}
                            onChange={(value) => {
                                d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                            }}
                        />,
                        <Select
                            key='city'
                            placeholder='Город доставки'
                            value={filter?.cityIds}
                            allowClear={true}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            style={{ width: 200 }}
                            showSearch
                            onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                            optionFilterProp='children'
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={cities.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IPreloadingLine> = [
            {
                title: 'ID',
                align: 'center',
                width: 80,
                dataIndex: 'consigneeCode',
            },
            {
                title: 'Маркировка',
                width: 200,
                render: (_, record) => {
                    return (
                        <Tooltip placement='topLeft' title={record.markingId ? '' : 'Маркировка не найдена'} color='var(--main-red)'>
                            <div style={{ color: record.markingId ? 'var(--main-green)' : 'var(--main-red)' }}>{record.markingCode}</div>
                        </Tooltip>
                    );
                },
            },
            {
                title: 'Город',
                width: 200,
                dataIndex: 'cityName',
            },
            {
                title: 'AWB',
                align: 'center',
                width: 130,
                render: (_, record) => {
                    return (
                        record.awbNumber && (
                            <Tag color='cyan' style={{ marginLeft: 10, color: '#000000' }}>
                                {record.awbNumber}
                            </Tag>
                        )
                    );
                },
            },
            {
                title: 'Страна',
                width: 100,
                dataIndex: 'countryName',
            },
            {
                title: 'Авиа',
                width: 100,
                align: 'center',
                dataIndex: 'countryName',
                render: (_, record) => {
                    return record.cargoCarrierName && <Tag color='var(--cargo-color)'>{record.cargoCarrierName}</Tag>;
                },
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                dataIndex: 'qty',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    if (record.qty) return record.qty;
                    return <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>0</span>;
                },
            },
            {
                title: 'Брутто / кг',
                align: 'center',
                dataIndex: 'grossWeight',
                width: 140,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    if (record.grossWeight) return record.grossWeight;

                    if (record.countryCode == 'NL') return;

                    return <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>0</span>;
                },
            },
            {
                title: 'Объемный / кг',
                align: 'center',
                dataIndex: 'volumeWeight',
                width: 140,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    if (record.volumeWeight) return record.volumeWeight;

                    if (record.countryCode == 'NL') return;

                    return <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>0</span>;
                },
            },
            {
                title: 'Продукт',
                width: 190,
                align: 'center',
                render: (_, record) => {
                    return (
                        <Tag
                            color={record.itemColor ?? 'red'}
                            style={{
                                marginBottom: 2,
                                paddingBottom: 2,
                                width: 'fit-content',
                                fontSize: 15,
                                fontWeight: 600,
                            }}
                        >
                            {record.itemName ?? '- Не задан -'}
                        </Tag>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    columnWidth: 32,
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                }}
                columns={columns}
                dataSource={lines}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                onRow={(record) => {
                    return {
                        onClick: (event) => {
                            onSelectChange([record.id || '']);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: height, x: 'max-content' }}
                summary={(pageData) => {
                    let totalQty = 0;
                    let totalGrossWeight = 0;
                    let totalVolumeWeight = 0;

                    pageData.forEach(({ qty, grossWeight, volumeWeight }) => {
                        totalQty += qty || 0;
                        totalGrossWeight += grossWeight || 0;
                        totalVolumeWeight += volumeWeight || 0;
                    });

                    return (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={6} align='left'>
                                    ИТОГО
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5} align='center'>
                                    {totalQty}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6} align='center'>
                                    {totalGrossWeight}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={7} align='center'>
                                    {totalVolumeWeight}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={8}></Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 345px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {openTotals && (
                <Drawer
                    closable
                    destroyOnHidden
                    title={`Итого`}
                    placement='right'
                    open={true}
                    onClose={() => setOpenTotals(false)}
                    size={400}
                >
                    <PreloadingTotals api={api} serverFetch={serverFetch} />
                </Drawer>
            )}

            {modalCntextHolder}
        </div>
    );
};

export default ConsignmentImportPreloading;
